/*
 * 我的。
 *
 * 按数据所有权拆成三块（features/profile/README.md）：
 *   Account Profile   姓名、头像、性别、生日、简介
 *   Verified Identity 手机、邮箱（变更必须重新验证）
 *   Relationship      对角色的期待 wish
 *
 * ⚠ 这一页删掉过的东西，别加回来：
 *
 *   - **注销账号**。曾经摆了一个禁用的「注销账号」，却没有「退出账号」。
 *     日常要用的是退出，注销是一辈子点一次的破坏性操作，两者摆反了。
 *     注销真要做也不该是这里一个灰按钮，得走独立的确认与冷静期流程。
 *   - **导出我的数据**。没有这个功能，也不需要。
 *   - **「修改资料」按钮**。改哪一行就点哪一行，底下再放一个总的修改键，
 *     用户不知道它会改什么。
 *   - **会话历史入口**。它已有自己的入口，摆在这里是重复的。
 *
 * 灵力数值仍从左上角打开购买弹窗；灵力流水与充值订单则按产品要求
 * 在「我的」保留独立入口，不能再塞回购买弹窗。
 *
 * ⚠ **设置入口是第三个被一起删掉的，已经加回来了，别再删。**
 *   `/settings` 没有任何别的入口，而 **AI 生成内容告知**就在那一页里——
 *   `docs/CHARACTER.md` §5.2 与 CHAR-024 把它列为发布阻断项。
 *   记忆开关同样只有那一个入口。
 *
 * ## 保存是逐行的，不是整表提交
 *
 * 点一行 → 弹层 → 只 PATCH 那一个键。**不要改成一次提交整个表单**：
 * 服务端按 PATCH 语义处理（缺席=不动，空串=清空），整表提交会把
 * 当时没渲染的字段一起清掉。
 *
 * 哪些字段能改由服务端的 `editableFields` 决定，前端不写死——
 * `bio` / `wish` / `preferredName` 挂在关系上，没有角色上下文时写不了。
 *
 * 例外是**头像**：它不走弹层，点那一行直接弹系统相册。见 `pickAvatar`。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Butterfly,
  LoadingState,
  PageNotice,
} from "../../components/AsyncState";
import { Button, PageShell, Sheet } from "../../components/ui";
import { IconForward, IconRetry, IconUser } from "../../components/Icons";
import { ApiError } from "../../services/api-client";
import {
  confirmChannelBinding,
  fetchChannelBindings,
  rejectChannelBinding,
  revokeChannelBinding,
  startChannelBinding,
  type ChannelBindingProvider,
  type ChannelBindingView,
  type StartedChannelBinding,
} from "../../services/channel-binding-api";
import {
  fetchProfile,
  patchProfile,
  uploadAvatar,
  type ProfilePatch,
  type ProfileView,
} from "../../services/me-api";
import { useResource } from "../../state/resource";
import { useSession } from "../../state/session";
import "../pages.css";

/** 与服务端 `me-write.service.ts` 的上限逐条对齐。对不上会在提交时才被拒。 */
const LIMITS = { displayName: 24, preferredName: 24, homeCity: 32, bio: 200, wish: 400 };

/*
 * 界面上只给「不填 / 男 / 女」三档。
 *
 * ⚠ **服务端的 `Gender` 枚举仍是四个值**（male / female / other / undisclosed），
 * `profilePatchSchema` 也照收——这里删的是按钮，不是数据契约。存量里真有
 * 这两档的话读侧照样显示得出来。
 *
 * 删掉「其他」「不想说」的依据：全库 1,500 条 Profile 实测
 * `other` 0 条、`undisclosed` 0 条（null 1375 / male 89 / female 36），
 * 两年零使用。而且「不填」和「不想说」并排时没人分得清区别——
 * 前者是没说，后者也是没说。
 *
 * 顺序按 male → female，与 Prisma 枚举一致，也与大多数表单一致。
 */
const GENDERS = [
  { code: "male", label: "男" },
  { code: "female", label: "女" },
] as const;

/** 界面上不出现、但服务端仍然接受的两档。类型上要留着，存量数据可能有。 */
type GenderCode = "male" | "female" | "other" | "undisclosed";

/**
 * 资料里的一行。
 *
 * `editor` 决定点开之后给什么控件；`null` 表示这一项不在这里改
 * （手机号要走验证流程、微信要走授权流程），点开只说明该怎么改。
 */
interface Field {
  key: string;
  label: string;
  /** 展示值。空值时是「还没写」这类占位串，不能拿它回填输入框。 */
  value: string;
  hint: string;
  editor: "text" | "textarea" | "gender" | "date" | null;
  /** 回填用的原始值。占位串不能当初值——用户一点保存就把占位串存进去了。 */
  raw?: string;
  max?: number;
  placeholder?: string;
}

type BindingPanelItem = ChannelBindingView &
  Partial<Pick<StartedChannelBinding, "code">>;

export function ProfilePage() {
  const session = useSession();
  const data = useResource<ProfileView>((signal) => fetchProfile(signal), []);
  const profile = data.state.phase === "ready" ? data.state.data : null;
  const failed = data.state.phase === "failed";

  /* 点开的是哪一行。null 表示没开。 */
  const [editing, setEditing] = useState<Field | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * 站外身份绑定。
   *
   * `code` 只存在本页内存里：服务端从不保存验证码明文，所以刷新页面后
   * 只能重新生成。轮询只在等待平台提交的十分钟窗口里开启；一旦收到提交，
   * 立刻停下并让用户核对昵称/群，再由用户亲手确认，避免别人抢先发送就绑走。
   */
  const [bindingOpen, setBindingOpen] = useState(false);
  const [bindings, setBindings] = useState<ChannelBindingView[]>([]);
  const [bindingCurrent, setBindingCurrent] = useState<BindingPanelItem | null>(
    null,
  );
  const [bindingBusy, setBindingBusy] = useState(false);
  const [bindingError, setBindingError] = useState<string | null>(null);
  const [bindingCopied, setBindingCopied] = useState(false);

  const applyBindingRows = useCallback((rows: ChannelBindingView[]) => {
    setBindings(rows);
    setBindingCurrent((current) => {
      if (!current) {
        return rows.find((row) => row.status === "pending_confirmation") ?? null;
      }
      const refreshed = rows.find((row) => row.id === current.id);
      if (!refreshed) return null;
      return {
        ...refreshed,
        ...(current.code ? { code: current.code } : {}),
      };
    });
  }, []);

  const loadBindings = useCallback(async (signal?: AbortSignal) => {
    try {
      const rows = await fetchChannelBindings(signal);
      applyBindingRows(rows);
      setBindingError(null);
    } catch (error) {
      if (signal?.aborted) return;
      setBindingError(
        error instanceof ApiError ? error.message : "没能读取绑定状态，再试一次？",
      );
    }
  }, [applyBindingRows]);

  useEffect(() => {
    if (!bindingOpen || bindingCurrent?.status !== "awaiting_submission") return;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | null = null;
    const poll = async () => {
      await loadBindings(controller.signal);
      if (!stopped) timer = window.setTimeout(() => void poll(), 1_500);
    };
    timer = window.setTimeout(() => void poll(), 1_500);
    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      controller.abort();
    };
  }, [bindingCurrent?.status, bindingOpen, loadBindings]);

  const openBindings = () => {
    setBindingOpen(true);
    setBindingError(null);
    void loadBindings();
  };

  const beginBinding = async (providerKey: ChannelBindingProvider) => {
    setBindingBusy(true);
    setBindingError(null);
    setBindingCopied(false);
    try {
      const started = await startChannelBinding(providerKey);
      setBindingCurrent(started);
      setBindings((current) => [
        started,
        ...current.filter((row) => row.id !== started.id),
      ]);
    } catch (error) {
      setBindingError(
        error instanceof ApiError ? error.message : "没能生成绑定码，再试一次？",
      );
    } finally {
      setBindingBusy(false);
    }
  };

  const copyBindingCode = async () => {
    const code = bindingCurrent?.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // 部分微信/QQ WebView 没有 Clipboard API，保留一次同步复制兜底。
      const input = document.createElement("textarea");
      input.value = code;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setBindingCopied(true);
  };

  const confirmBinding = async () => {
    if (!bindingCurrent) return;
    setBindingBusy(true);
    setBindingError(null);
    try {
      const confirmed = await confirmChannelBinding(bindingCurrent.id);
      setBindingCurrent(confirmed);
      await loadBindings();
    } catch (error) {
      setBindingError(
        error instanceof ApiError ? error.message : "没能确认绑定，再试一次？",
      );
    } finally {
      setBindingBusy(false);
    }
  };

  const rejectBinding = async () => {
    if (!bindingCurrent) return;
    setBindingBusy(true);
    setBindingError(null);
    try {
      await rejectChannelBinding(bindingCurrent.id);
      setBindingCurrent(null);
      await loadBindings();
    } catch (error) {
      setBindingError(
        error instanceof ApiError ? error.message : "没能拒绝这次绑定，再试一次？",
      );
    } finally {
      setBindingBusy(false);
    }
  };

  const unbind = async (binding: ChannelBindingView) => {
    if (
      !window.confirm(
        `确定解除 ${binding.platformLabel} 绑定吗？解除后，新的站外经历不会再归到这个账号。`,
      )
    ) {
      return;
    }
    setBindingBusy(true);
    setBindingError(null);
    try {
      await revokeChannelBinding(binding.id);
      if (bindingCurrent?.id === binding.id) setBindingCurrent(null);
      await loadBindings();
    } catch (error) {
      setBindingError(
        error instanceof ApiError ? error.message : "没能解除绑定，再试一次？",
      );
    } finally {
      setBindingBusy(false);
    }
  };

  /*
   * 换头像。
   *
   * ⚠ **没有中间那层弹层了。** 点头像那一行直接弹系统相册。
   * 原来是「点行 → 弹层（一句说明 + 一颗「从相册里挑一张」）→ 再弹相册」，
   * 中间那一层不承载任何选择——它只有一颗按钮，用户没得选，
   * 唯一的作用是让每次换头像都多点一下。
   *
   * ⚠ **不做本地压缩。** 看起来很划算（省流量、省存储），但 canvas 重编码会
   * 在低端机上悄悄改变色彩与朝向（EXIF orientation 丢失，竖着拍的照片变横的），
   * 而用户完全不知道发生了什么。服务端有 MIME 白名单与大小上限，超了就明确
   * 拒绝——「传不上去」比「传上去变了样」好解释得多。真要压缩得先解决 EXIF。
   *
   * 弹层里那句格式说明（PNG/JPG/WebP/GIF、不超过 N MB）随弹层一起没了，
   * 这是有意的：`accept` 已经让相册默认只列出图片，真传错了服务端会回一句
   * 明确的话（「图片不能超过 10 MB」），就显示在这一行上。
   * ⚠ 要是想把它加回来，**别把数字写死**——上限是服务端的 `ASSET_MAX_BYTES`，
   * 这句文案曾经写着 5 MB 而实际是 10 MB，于是 7 MB 的照片明明传得上去，
   * 界面却先把人劝退了。
   */
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const pickAvatar = (file: File | undefined) => {
    if (!file) return;
    setAvatarBusy(true);
    setAvatarError(null);
    uploadAvatar(file)
      .then(() => {
        data.reload();
        // 账号头像也挂在会话上（`SessionValue.accountAvatarUrl`），右上角那一排
        // 与聊天里用户那侧的头像都读它。只 reload 这一页的话，别处还是旧图。
        session.reload();
      })
      .catch((e: unknown) => {
        setAvatarError(e instanceof ApiError ? e.message : "没能传上去，再试一次？");
      })
      .finally(() => {
        setAvatarBusy(false);
        // 清空 input，否则连续选同一个文件不会触发 change。
        if (avatarInputRef.current) avatarInputRef.current.value = "";
      });
  };

  /** 服务端说了算，不在前端写死哪些能改。 */
  const canEdit = (key: string) => profile?.editableFields.includes(key) ?? false;

  const openEditor = (field: Field) => {
    setEditing(field);
    setDraft(field.raw ?? "");
    setSaveError(null);
  };

  const save = async () => {
    if (!editing || !editing.editor) return;

    // 没改动就不发请求，直接关掉。见按钮那里的注释。
    if (draft.trim() === (editing.raw ?? "")) {
      setEditing(null);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      // ⚠ 只送这一个键。整表提交会把当时没渲染的字段一起清空——
      // 服务端按 PATCH 语义处理，缺席=不动，空串=清空。
      const value = draft.trim();
      const patch: ProfilePatch =
        editing.editor === "gender"
          ? // 空串 = 不填，落成 null。不能写成 `value as ...`——
            // exactOptionalPropertyTypes 下那样会把 undefined 混进来，
            // 而 undefined 在 PATCH 语义里是「不动」，与「清空」正好相反。
            { gender: value === "" ? null : (value as GenderCode) }
          : editing.editor === "date"
            ? { birthday: value || null }
            : ({ [editing.key]: value } as ProfilePatch);
      await patchProfile(patch);
      setEditing(null);
      data.reload();
    } catch (error) {
      // 服务端的话直接用（「这个称呼不能用：角色名」比「保存失败」有用得多）。
      setSaveError(error instanceof ApiError ? error.message : "没能存上，再试一次？");
    } finally {
      setSaving(false);
    }
  };

  const fields: Field[] = profile
    ? [
        {
          key: "displayName",
          label: "昵称",
          value: profile.displayName || "还没起名字",
          raw: profile.displayName,
          hint: "改完之后，缈缈叫你的时候就会换成新名字。",
          editor: "text",
          max: LIMITS.displayName,
          placeholder: "比如：小明",
        },
        {
          key: "gender",
          label: "性别",
          value: profile.gender ?? "没有填",
          raw: profile.genderCode ?? "",
          hint: "只用来让缈缈说话时不叫错，不填也完全没关系。",
          editor: "gender",
        },
        {
          key: "birthday",
          label: "生日",
          value: profile.birthday ?? "没有填",
          raw: profile.birthday ?? "",
          hint: "到了那天，缈缈会记得。",
          editor: "date",
        },
        {
          key: "homeCity",
          label: "常住城市",
          value: profile.homeCity ?? "没有填",
          raw: profile.homeCity ?? "",
          hint: "填了之后她聊到天气、季节的时候会说得准一些。",
          editor: "text",
          max: LIMITS.homeCity,
          placeholder: "比如：上海",
        },
        {
          key: "maskedPhone",
          label: "手机号",
          value: profile.maskedPhone ?? "未绑定",
          hint: "手机号是登录用的，换号要先用短信验证码验证新号码。",
          editor: null,
        },
        {
          key: "wechat",
          label: "微信",
          value: profile.wechatBound ? "已绑定" : "未绑定",
          hint: "绑定之后可以直接用微信进来，不用再等验证码。",
          editor: null,
        },
        /* 下面三项挂在关系上，对每个角色可以不一样。 */
        {
          key: "preferredName",
          label: "她怎么称呼我",
          value: profile.preferredName ?? "还没说过",
          raw: profile.preferredName ?? "",
          hint: "想让缈缈怎么叫你？留空她就不特意称呼你了。",
          editor: "text",
          max: LIMITS.preferredName,
          placeholder: "比如：小明哥哥",
        },
        {
          key: "bio",
          label: "自我介绍",
          value: profile.bio ?? "还没写",
          raw: profile.bio ?? "",
          hint: "写点你想让缈缈知道的事，她聊天的时候会放在心上。",
          editor: "textarea",
          max: LIMITS.bio,
          placeholder: "比如：我是个爱看星星的程序员",
        },
        {
          key: "wish",
          label: "期待",
          value: profile.wish ?? "还没说过",
          raw: profile.wish ?? "",
          hint: "你希望缈缈是什么样子的？她会参考着调整——但不会因此变成另一个人。",
          editor: "textarea",
          max: LIMITS.wish,
          placeholder: "比如：多问问我今天过得怎么样",
        },
      ]
    : [];

  const tooLong = editing?.max !== undefined && draft.trim().length > editing.max;
  /** 值和打开时不一样。保存按钮据此变成主色，也是「要不要发请求」的判据。 */
  const dirty = editing !== null && draft.trim() !== (editing.raw ?? "");
  /** 这一项服务端当前允不允许写。不允许时弹层只解释原因，不给输入框。 */
  const writable = editing !== null && editing.editor !== null && canEdit(editing.key);

  return (
    <PageShell
      icon={<IconUser size={20} />}
      title="我的"
      actions={
        failed ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? (
        <LoadingState label="正在读取资料" />
      ) : null}

      {/* 这里必须写完整判断而不是用上面的 failed：布尔量不参与类型收窄，
          data.state.error 会被当成可能为 null。 */}
      {data.state.phase === "failed" ? (
        <PageNotice
          scene="profile"
          error={data.state.error}
          onRetry={data.reload}
          expected={[
            "昵称、头像、性别、生日与简介",
            "已验证的手机号与邮箱（默认脱敏显示）",
            "你对缈缈的期待 wish",
          ]}
        />
      ) : null}

      {profile ? (
        <>
          {/*
            换头像用的文件选择器。**放在 <dl> 外面**：`<dl>` 的合法子元素只有
            dt / dd / div / script / template，塞个 input 进去虽然因为 hidden
            不会显示，但它是无效结构，也可能被将来某条
            `.kv-list > *` 的样式意外命中。

            `accept` 只是给系统文件选择器的提示，**不是校验**：用户能绕过它，
            服务端那份 MIME 白名单才是权威。这里写上只为让相册默认过滤。
          */}
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(event) => pickAvatar(event.target.files?.[0])}
          />

          <dl className="kv-list">
            {/*
              头像是资料的第一行，不再单独占一块头卡。
              点它**直接弹相册**，不经过任何中间弹层——见 pickAvatar 的注释。
            */}
            <button
              type="button"
              className="kv-list__row"
              disabled={avatarBusy}
              onClick={() => avatarInputRef.current?.click()}
            >
              <dt>头像</dt>
              <dd className="profile-avatar-cell">
                {avatarError ? (
                  <span className="edit-error">{avatarError}</span>
                ) : null}
                {/*
                  上传走 60 秒超时（移动网络传数 MB 是常事），纯文案切换撑不住
                  这个时长——用户会以为按下去没反应。这里给一只小蝴蝶。
                */}
                {avatarBusy ? (
                  <span className="inline-hint">
                    <Butterfly className="butterfly--inline" />
                    正在上传…
                  </span>
                ) : null}
                {/*
                  画的是**用户自己的**头像（`accountAvatarUrl`），
                  不是 `useAppearance().avatarUrl`——那是角色的脸。
                  拿不到就画占位图标，不回落到角色头像。
                */}
                <span className="profile-avatar profile-avatar--inline" aria-hidden>
                  {session.accountAvatarUrl ? (
                    <img src={session.accountAvatarUrl} alt="" />
                  ) : (
                    <IconUser size={20} />
                  )}
                </span>
              </dd>
            </button>

            {fields.map((field) => (
              <button
                key={field.key}
                type="button"
                className="kv-list__row"
                onClick={() => openEditor(field)}
              >
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </button>
            ))}

            <button
              type="button"
              className="kv-list__row"
              onClick={openBindings}
            >
              <dt>绑定其他平台</dt>
              <dd>
                QQ、B站等
                <IconForward size={16} />
              </dd>
            </button>

            {/* 加入时间只读，没有「怎么改」可说，所以不做成可点的行。 */}
            <div>
              <dt>加入灵境</dt>
              <dd>{new Date(profile.joinedAt).toLocaleDateString("zh-CN")}</dd>
            </div>

            {/*
              设置的入口。

              ⚠ **不要再删掉它。** 设置没有其他入口；`/settings` 不能变成一个
              只能手打地址访问的页面。

              而 **AI 生成内容告知**就在那一页里（`components/AiDisclosure.tsx`）。
              `docs/CHARACTER.md` §5.2 与 `FEATURES.md` CHAR-024 把「三种宿主都必须
              展示」列为**发布阻断项**——用户点不到的告知等于没有告知。
              记忆开关也在那一页，同样只有这一个入口。

              用 Link 而不是 button + navigate：它要能被长按复制、能被 SEO 之外的
              任何东西当成一条真实的链接看待，也不必给 kv-list__row 再写一套
              键盘可达性。
            */}
            <Link className="kv-list__row" to="/relationship">
              <dt>灵力记录</dt>
              <dd>
                查看每笔灵力的获取与消耗
                <IconForward size={16} />
              </dd>
            </Link>

            <Link className="kv-list__row" to="/orders">
              <dt>我的订单</dt>
              <dd>
                查看灵力购买与到账记录
                <IconForward size={16} />
              </dd>
            </Link>

            <Link className="kv-list__row" to="/settings">
              <dt>设置</dt>
              <dd>
                梦境特效、记忆与隐私、关于缈缈
                <IconForward size={16} />
              </dd>
            </Link>
          </dl>
        </>
      ) : null}

      {/*
        退出账号只在**服务端确认过身份**时出现。
        连不上时前端读不到 httpOnly Cookie，无从判断到底登没登；这时摆一个退出键，
        点下去只会失败，还会让人以为自己已经退出了。判据用 session.appearance——
        它只可能由服务端下发写入。

        退出会请服务端撤销 Refresh Session，不是只清本地。旧版的 logout() 只做了
        localStorage.setItem('Authorization', '')，那个 30 天的 JWT 拷到别处照样能用
        ——退出等于没退。
      */}
      {session.appearance !== null ? (
        <Button
          tone="danger"
          block
          className="profile-signout"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            // 退出后 RequireSession 会把用户送回 /login，这里不用自己跳。
            void session.logout().finally(() => setBusy(false));
          }}
        >
          {busy ? "正在退出" : "退出账号"}
        </Button>
      ) : null}

      <Sheet
        open={bindingOpen}
        title="绑定其他平台"
        onClose={() => {
          setBindingOpen(false);
          if (bindingCurrent?.status === "active") setBindingCurrent(null);
        }}
      >
        <div className="channel-binding">
          <p className="help-text">
            各个平台都使用一次性 DV 验证码。每个验证码只绑定一个平台身份，
            被正确发送一次后就立刻失效，还要回到这里由你确认。
          </p>

          {bindings.some((binding) => binding.status === "active") ? (
            <section className="channel-binding__section">
              <h3>已绑定</h3>
              <div className="channel-binding__list">
                {bindings
                  .filter((binding) => binding.status === "active")
                  .map((binding) => (
                    <div className="channel-binding__linked" key={binding.id}>
                      <div>
                        <strong>{binding.platformLabel}</strong>
                        <span>
                          {binding.actorDisplayName || "已验证身份"}
                          {binding.scopeLabel ? ` · ${binding.scopeLabel}` : ""}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="channel-binding__unlink"
                        disabled={bindingBusy}
                        onClick={() => void unbind(binding)}
                      >
                        解除
                      </button>
                    </div>
                  ))}
              </div>
            </section>
          ) : null}

          {bindingCurrent?.status === "awaiting_submission" ? (
            <section className="channel-binding__section">
              <h3>去 {bindingCurrent.platformLabel} 发送绑定码</h3>
              {bindingCurrent.code ? (
                <>
                  <p className="channel-binding__instruction">
                    {bindingCurrent.providerKey === "bilibili"
                      ? "请在缈缈正在直播的 B站直播间，只发送下面的验证码。DreamLive 会把它作为私密操作指令处理，不会拿去生成直播回复。"
                      : "请私聊 QQ 中的「缈缈」，只发送下面的验证码。若在已配置的 QQ 群里发送，请先 @缈缈，以确保消息会被机器人收到。"}
                  </p>
                  <div className="channel-binding__command">
                    <code>{bindingCurrent.code}</code>
                    <Button
                      tone="primary"
                      disabled={bindingBusy}
                      onClick={() => void copyBindingCode()}
                    >
                      {bindingCopied ? "已复制" : "复制"}
                    </Button>
                  </div>
                  <p className="inline-hint">
                    10 分钟内有效。正确发送一次后，这个码就不能再次使用。
                  </p>
                </>
              ) : (
                <>
                  <p className="channel-binding__instruction">
                    为了安全，旧绑定码的明文不会保存在服务器，也不能再次显示。
                  </p>
                  <Button
                    tone="primary"
                    block
                    disabled={bindingBusy}
                    onClick={() => void beginBinding(
                      bindingCurrent.providerKey as ChannelBindingProvider,
                    )}
                  >
                    重新获取绑定码
                  </Button>
                </>
              )}
            </section>
          ) : null}

          {bindingCurrent?.status === "pending_confirmation" ? (
            <section className="channel-binding__section">
              <h3>是你刚才发送的吗？</h3>
              <div className="channel-binding__candidate">
                <strong>
                  {bindingCurrent.actorDisplayName
                    || `${bindingCurrent.platformLabel}用户`}
                </strong>
                <span>
                  {bindingCurrent.scopeLabel || bindingCurrent.platformLabel}
                </span>
              </div>
              <p className="inline-hint">
                只有点「确认是我」后，今后在这个平台的经历才会与你的账号关联。
              </p>
              <div className="channel-binding__actions">
                <Button
                  tone="primary"
                  block
                  disabled={bindingBusy}
                  onClick={() => void confirmBinding()}
                >
                  {bindingBusy ? "正在确认" : "确认是我"}
                </Button>
                <Button
                  tone="ghost"
                  block
                  disabled={bindingBusy}
                  onClick={() => void rejectBinding()}
                >
                  不是我
                </Button>
              </div>
            </section>
          ) : null}

          {bindingCurrent?.status === "active" ? (
            <div className="channel-binding__success" role="status">
              <strong>{bindingCurrent.platformLabel} 已绑定</strong>
              <span>以后这个身份产生的经历可以安全地归到你的回忆里。</span>
            </div>
          ) : null}

          {!bindingCurrent ? (
            <div className="channel-binding__provider-actions">
              {([
                ["bilibili", "B站"],
                ["qq_official", "QQ"],
              ] as const).map(([providerKey, label]) => (
                <Button
                  key={providerKey}
                  tone="primary"
                  block
                  disabled={bindingBusy}
                  onClick={() => void beginBinding(providerKey)}
                >
                  {bindingBusy
                    ? "正在生成"
                    : bindings.some(
                        (binding) =>
                          binding.providerKey === providerKey
                          && binding.status === "active",
                      )
                      ? `绑定另一个${label}身份`
                      : `绑定${label}`}
                </Button>
              ))}
            </div>
          ) : null}

          <p className="channel-binding__future">
            QQ 与 B站要分别使用新的验证码；B站只有在直播和弹幕连接都在线时才能收到。
          </p>
          {bindingError ? <p className="edit-error">{bindingError}</p> : null}
        </div>
      </Sheet>

      <Sheet
        open={editing !== null}
        title={editing?.label ?? ""}
        onClose={() => setEditing(null)}
      >
        {editing ? (
          <form
            className="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (writable && !saving && !tooLong) void save();
            }}
          >
            <p className="help-text">{editing.hint}</p>

            {!writable ? (
              <p className="inline-hint">
                {editing.editor === null
                  ? "这一项要走单独的验证流程，不能在这里直接改。"
                  : "这一项要先和缈缈说上话才能设置。"}
              </p>
            ) : editing.editor === "gender" ? (
              <div className="chip-row">
                {/* 「不填」是一个真实选项：服务端 gender 可为 null。
                    它同时也吸收了原先「不想说」那一档——两个并排没人分得清。 */}
                {[{ code: "", label: "不填" }, ...GENDERS].map((g) => (
                  <button
                    key={g.code}
                    type="button"
                    className={`chip${draft === g.code ? " is-on" : ""}`}
                    onClick={() => setDraft(g.code)}
                    disabled={saving}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            ) : editing.editor === "date" ? (
              <BirthdayPicker
                value={draft}
                disabled={saving}
                onChange={setDraft}
              />
            ) : editing.editor === "textarea" ? (
              <textarea
                className={`field field--area${tooLong ? " is-bad" : ""}`}
                value={draft}
                rows={4}
                placeholder={editing.placeholder}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
              />
            ) : (
              <input
                className={`field${tooLong ? " is-bad" : ""}`}
                value={draft}
                placeholder={editing.placeholder}
                onChange={(e) => setDraft(e.target.value)}
                // maxLength 不设死，让超长可见并给出提示——
                // 悄悄截断会让用户以为自己打错了。
                enterKeyHint="done"
                disabled={saving}
                autoFocus={!window.matchMedia?.("(pointer: coarse)").matches}
              />
            )}

            {writable && editing.max !== undefined ? (
              <p className={`edit-count${tooLong ? " is-bad" : ""}`}>
                {draft.trim().length} / {editing.max}
              </p>
            ) : null}

            {saveError ? <p className="edit-error">{saveError}</p> : null}

            {/*
              ⚠ **没有「取消」。** 关掉弹层的路只留右上角那颗叉与点遮罩，
              和这一页所有别的弹层一致。摆一个「取消」在「保存」旁边，
              等于让两个同样大小的按钮争一次点击，而其中一个什么都不做。

              保存按钮的深浅表达的是**有没有东西可存**：改动过才是主色。
              没改动时点它不发请求，直接关掉——PATCH 一个没变的值在服务端
              是空操作，但它会真的走一遍昵称保留字校验并回一次 reload，
              为一次「用户看了看又没改」付这些代价没有道理。
            */}
            {writable ? (
              <div className="edit-actions">
                <Button
                  type="submit"
                  tone={dirty ? "primary" : "ghost"}
                  block
                  disabled={saving || tooLong}
                >
                  {saving ? "正在保存" : "保存"}
                </Button>
              </div>
            ) : null}
          </form>
        ) : null}
      </Sheet>
    </PageShell>
  );
}

/* -------------------------------------------------------------------------- */

/** 能选到的最早年份。1900 之前的生日不是真实输入，是误触。 */
const BIRTH_MIN_YEAR = 1900;

/** 某年某月有几天。month 是 1–12。0 号即上个月最后一天，闰年不用自己判。 */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

interface BirthdayParts {
  year: number | null;
  month: number | null;
  day: number | null;
}

function birthdayParts(value: string): BirthdayParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match
    ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    : { year: null, month: null, day: null };
}

/**
 * 生日：三个下拉，年 / 月 / 日。
 *
 * ## 为什么不用 `<input type="date">`
 *
 * 它的分段占位文字**由浏览器按自己的界面语言渲染，页面完全控制不了**——
 * 既不能改文案，也不能用 CSS 选中。实测中文 Windows 上出现过
 * 「yyyy/mm/日」这种前两段英文、第三段中文的混排，而文档上写着
 * `lang="zh-CN"` 也不影响它。这不是我们的 bug，但也不是我们能修的 bug，
 * 唯一可控的做法是不用它。
 *
 * 换成三个 `<select>` 之后：文案是我们自己的，三段一致；移动端弹的是系统
 * 滚轮，比日历面板更好点；而且天数跟着年月走，选不出 2 月 30 日。
 *
 * ## 三段必须一起完整才算数
 *
 * 缺任何一段时只保留在控件自己的临时状态里，不回传给表单。半个日期（只选了年）
 * 既存不进 `YYYY-MM-DD`，也表达不了任何意思，不能让它悄悄变成「1998-01-01」。
 * 控件不能只从完整的 value 反推三段：那样第一次选年时月、日仍为空，回传空串后
 * 年份会立刻重置，用户永远填不完。
 *
 * 换月/换年可能让当前的「日」越界（3 月 31 日 → 2 月），也可能让日期跑到未来
 * （选了今年之后再看 12 月）。两种情况都**主动夹回合法范围**而不是清空或报错：
 * 服务端确实会拒（`INVALID_BIRTHDAY`「生日不能是未来」），但让用户选完再被拒
 * 不如一开始就选不出来。
 */
function BirthdayPicker({
  value,
  disabled,
  onChange,
}: {
  /** `YYYY-MM-DD`，空串表示没填。 */
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const today = new Date();
  const maxYear = today.getFullYear();
  const maxMonth = today.getMonth() + 1;
  const maxDay = today.getDate();

  const [parts, setParts] = useState<BirthdayParts>(() => birthdayParts(value));
  useEffect(() => setParts(birthdayParts(value)), [value]);
  const { year, month, day } = parts;

  const years: number[] = [];
  for (let y = maxYear; y >= BIRTH_MIN_YEAR; y--) years.push(y);

  // 今年只到本月，本年本月只到今天——生日不可能在未来。
  const monthCount = year === maxYear ? maxMonth : 12;
  const months: number[] = [];
  for (let m = 1; m <= monthCount; m++) months.push(m);

  // 年月还没齐时按 31 天列出，避免「必须先选年才能看到 31 号」。
  const dayCount =
    year !== null && month !== null
      ? year === maxYear && month === maxMonth
        ? maxDay
        : daysInMonth(year, month)
      : 31;
  const days: number[] = [];
  for (let d = 1; d <= dayCount; d++) days.push(d);

  const pad = (n: number) => String(n).padStart(2, "0");

  const emit = (y: number | null, m: number | null, d: number | null) => {
    // 先夹月（换到今年时 12 月要退回本月），再按夹完的年月夹日。
    const cm = y === maxYear && m !== null ? Math.min(m, maxMonth) : m;
    const limit =
      y !== null && cm !== null
        ? y === maxYear && cm === maxMonth
          ? maxDay
          : daysInMonth(y, cm)
        : null;
    const cd = d !== null && limit !== null ? Math.min(d, limit) : d;
    setParts({ year: y, month: cm, day: cd });
    if (y !== null && cm !== null && cd !== null) {
      onChange(`${y}-${pad(cm)}-${pad(cd)}`);
    } else if (value !== "") {
      onChange("");
    }
  };

  return (
    <div className="date-parts">
      <label className="date-parts__part">
        <select
          className="field field--select"
          value={year ?? ""}
          disabled={disabled}
          onChange={(e) =>
            emit(e.target.value ? Number(e.target.value) : null, month, day)
          }
        >
          <option value="">----</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <span className="date-parts__unit">年</span>
      </label>

      <label className="date-parts__part">
        <select
          className="field field--select"
          value={month ?? ""}
          disabled={disabled}
          onChange={(e) =>
            emit(year, e.target.value ? Number(e.target.value) : null, day)
          }
        >
          <option value="">--</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <span className="date-parts__unit">月</span>
      </label>

      <label className="date-parts__part">
        <select
          className="field field--select"
          value={day ?? ""}
          disabled={disabled}
          onChange={(e) =>
            emit(year, month, e.target.value ? Number(e.target.value) : null)
          }
        >
          <option value="">--</option>
          {days.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <span className="date-parts__unit">日</span>
      </label>
    </div>
  );
}
